// The comprehension objection — "did Loom UNDERSTAND what the user asked for?"
//
// Distinct from grounding. Grounding proves every byte in a slot came from the
// user; it says nothing about whether the byte landed in the RIGHT slot. Live
// issue #78 passed every anti-fabrication mechanism with the user's PROBLEM
// ("the dashboard is hard to scan when a project has a lot of issues") sitting
// in the `ends` slot as the desired OUTCOME. Verbatim, provenanced, and wrong.
//
// The objection that would have caught it already existed: `quality_check`'s
// QUS rubric criterion `qus.conceptually_sound` ("the means is an action the
// role can take; the ends is a value the role gains. Means and ends are not
// swapped."). It had no reader. This module is the selection rule the readers
// share.
//
// WHY A SINGLE CRITERION ID, NOT THE FAMILY. Measured 2026-08-03 (108 reps over
// 9 well-formed stories, `claude-sonnet-5` @ `xhigh`, end-to-end through
// `quality_check`): `qus.conceptually_sound` fired non-ok on 0/108 good stories
// and 12/12 on #78. The rest of the family is a warn machine on healthy
// stories — `qus.minimal` 80/108 non-ok, `qus.problem_oriented` 57/108,
// `qus.atomic` 48/108; any-criterion-non-ok is 91%. Keying a consumer on the
// family, or on a rolled-up score, ships a nag.
//
// WHY NON-OK, NOT `level === "fail"`. Same measurement: non-ok is 36/36 on #78
// across every condition ever run; fail-only is 24/36. Non-ok costs nothing on
// the good corpus (0/108), so there is no measured reason to prefer fail.
//
// WHY THE MODEL'S OWN `suggestion`, NEVER TEMPLATED COPY. The criterion's LABEL
// BLEEDS: on a `symptom-as-cause` story it fired with reasoning that is really
// a `problem_oriented` argument. The id therefore does not reliably describe
// what the model objected to; only the model's own sentence does. Consumers
// MUST pass `evidence` / `suggestion` through verbatim and MUST NOT synthesise
// copy from `criterion`.
//
// WHAT A CONSUMER MAY DO WITH `suggestion`. It is a REPHRASING PROPOSAL
// ("so that the dashboard is easy to scan when a project has many issues").
// It is model prose ABOUT the user's words. It may be shown, and it may prompt
// a QUESTION whose answer the user authors — it may never reach a slot `value`,
// because that would mint a "user-approved rephrasing" provenance class this
// codebase does not have.
//
// WHY THE WORKED EXAMPLE IS CUT OFF IT. Live on issue #81 the advisory read
// "Finish the ends clause to state the value gained, e.g. 'so that I can find
// the issue by a phrase I remember from its description.'" — and the outcome the
// user then hand-typed opened with the same five words. Nothing was violated:
// the user typed it, the gate grounded it in their own turn, the provenance
// chain correctly says "user's own words". That is the problem. A sentence the
// user reads and re-types is indistinguishable, downstream, from one they
// thought of, so the ghost-written half of the suggestion is the one place this
// design can put words in a user's mouth without any mechanism noticing.
// `dropWorkedExample` removes that clause and keeps the DIAGNOSIS, which is the
// half that has to stay verbatim (the label bleeds — see above).

import { z } from "zod";

/** The one QUS criterion a comprehension consumer may key on. */
export const COMPREHENSION_CRITERION_ID = "qus.conceptually_sound";

/**
 * A single non-ok comprehension verdict, carried verbatim from the judge.
 *
 * `level` accepts `"ok"` for parse tolerance only (the persisted `CardState` is
 * read with a strict `safeParse`, so a narrower enum would make one odd row
 * throw away the whole card). The producer never writes an `ok` verdict here,
 * and both projections drop one defensively — see `selectComprehensionObjection`.
 */
export const comprehensionObjectionSchema = z.object({
  /** Rubric criterion id, e.g. `qus.conceptually_sound`. NOT copy. */
  criterion: z.string(),
  level: z.enum(["ok", "warn", "fail"]),
  /** The span of the artifact the judge objected to. Verbatim. */
  evidence: z.string(),
  /** The judge's own correction sentence. Verbatim, or null. */
  suggestion: z.string().nullable(),
});
export type ComprehensionObjection = z.infer<typeof comprehensionObjectionSchema>;

// ---------------------------------------------------------------------------
// The worked-example cut.
//
// The judge writes free prose, so the clause has no fixed shape. These patterns
// are therefore deliberately narrow: an EXPLICIT example marker, at a clause or
// sentence boundary, and everything from there to the end of the string. What
// that buys is the guarantee the negative case needs — a suggestion with no such
// marker comes back BYTE-IDENTICAL, so the ordinary diagnosis is never mangled
// by a cut that guessed.
//
// What it does NOT catch, stated plainly rather than left to be discovered: an
// example introduced by nothing but a colon and a quote, and a suggestion that
// is nothing BUT a rewrite with no diagnosis in front of it (the #78 verdict
// itself — "so that the dashboard is easy to scan…" — is exactly that shape).
// Both are the same seeding risk. Neither is separable from a legitimate
// diagnosis by shape alone, and a cut that guessed at them would silently eat
// the objection the user needs to read.
const EXAMPLE_MARKER = String.raw`(?:e\.?\s?g\.?|for\s+example|for\s+instance)`;
/** A marker is only a marker when a word does not continue through it ("egg"). */
const MARKER_END = String.raw`(?![A-Za-z0-9])`;

/** The whole suggestion is the example — there is no diagnosis to keep. */
const LEADING_EXAMPLE = new RegExp(`^\\s*${EXAMPLE_MARKER}${MARKER_END}`, "iu");

/** "…restates the problem. For example, '…'" — the sentence's own terminator is
 *  part of the diagnosis, so it is captured and put back. */
const SENTENCE_TAIL = new RegExp(
  `([.!?][)"'’”]?)\\s+${EXAMPLE_MARKER}${MARKER_END}[\\s\\S]*$`,
  "iu",
);

/** "…the value gained, e.g. '…'" / "… (e.g. …)" / "… — e.g. …" — the separator
 *  belongs to the example clause and goes with it. */
const CLAUSE_TAIL = new RegExp(
  `\\s*[,;:(—–-]\\s*${EXAMPLE_MARKER}${MARKER_END}[\\s\\S]*$`,
  "iu",
);

/**
 * Strip the judge's worked example off a suggestion, keeping its diagnosis.
 *
 * Returns `null` when nothing but the example is left (or was there to begin
 * with). `null` is the same "no correction sentence" the judge itself emits, and
 * every consumer already falls back to `evidence` — which is a span of the
 * ARTIFACT, i.e. the user's own words quoted back, and so cannot seed anything.
 *
 * Idempotent: the cut runs to the end of the string, so a second pass finds no
 * marker. Consumers re-select off an already-selected verdict
 * (`partitionMethodologyVerdicts` appends the selected objection to the hint
 * list the browser then re-reads), and that must be a no-op.
 */
export const dropWorkedExample = (
  suggestion: string | null,
): string | null => {
  if (suggestion === null) return null;
  if (LEADING_EXAMPLE.test(suggestion)) return null;
  const cut = suggestion.replace(SENTENCE_TAIL, "$1").replace(CLAUSE_TAIL, "");
  // No marker matched: hand back the original object's own bytes, untrimmed and
  // untouched. The negative case must be provably inert.
  if (cut === suggestion) return suggestion;
  const kept = cut.trim();
  return kept === "" ? null : kept;
};

/** The shape both `Verdict` (@loom/workflows) and `InvestVerdict` satisfy. */
export interface RubricVerdictLike {
  readonly criterion: string;
  readonly level: "ok" | "warn" | "fail";
  readonly evidence: string;
  readonly suggestion: string | null;
}

/**
 * Pick the comprehension objection out of a rubric run: the single
 * `qus.conceptually_sound` verdict, and only when it is NON-OK. Returns
 * `undefined` for "no objection" — absence is the quiet case everywhere, so a
 * consumer that has never heard of this field behaves exactly as before.
 *
 * Takes the first non-ok match; a rubric family emits one verdict per criterion,
 * so a second is a model duplicate, not a second opinion.
 *
 * `suggestion` is the judge's, minus its worked example (`dropWorkedExample`).
 * This is the ONE seam that cut lives on: the Stop hook reads the objection off
 * the persisted projection this produces, and the Focus advisory re-selects off
 * the hint list it produces, so neither surface can drift into showing the
 * ghost-written sentence. `evidence` is untouched — it is the user's own words.
 */
export const selectComprehensionObjection = (
  verdicts: readonly RubricVerdictLike[],
): ComprehensionObjection | undefined => {
  const hit = verdicts.find(
    (v) => v.criterion === COMPREHENSION_CRITERION_ID && v.level !== "ok",
  );
  if (!hit) return undefined;
  return {
    criterion: hit.criterion,
    level: hit.level,
    evidence: hit.evidence,
    suggestion: dropWorkedExample(hit.suggestion),
  };
};

/**
 * Split one `quality_check` run over `["invest","qus"]` into what the card
 * carries.
 *
 * `invest.verdicts` is the COACHING HINT LIST every card surface renders
 * (`ReadinessCard` shows the non-ok ones as `criterion: evidence`). It gets the
 * INVEST family plus AT MOST the one comprehension verdict — never the rest of
 * the QUS family, which warns on 44–74% of well-formed stories and would turn
 * the card into a nag box (measured; see the head of this file).
 *
 * `invest.score` stays the INVEST score: computed over the INVEST subset only,
 * with `quality_check`'s own formula (round(ok/total × 100), 0 on empty), so
 * wiring QUS in cannot move a number users read as "INVEST score". It is
 * therefore NOT the ok-ratio of the list above when an objection is appended —
 * deliberate, and the reason this function returns them as one pair.
 *
 * `comprehension` is the same objection, already selected, for consumers that
 * must not re-implement the selection rule (the Stop hook reads it off
 * `card_status`).
 */
export const partitionMethodologyVerdicts = (
  verdicts: readonly RubricVerdictLike[],
): {
  invest: { verdicts: RubricVerdictLike[]; score: number };
  comprehension?: ComprehensionObjection;
} => {
  const investVerdicts = verdicts.filter((v) =>
    v.criterion.startsWith("invest."),
  );
  const okCount = investVerdicts.filter((v) => v.level === "ok").length;
  const score =
    investVerdicts.length === 0
      ? 0
      : Math.round((okCount / investVerdicts.length) * 100);
  const comprehension = selectComprehensionObjection(verdicts);
  return {
    invest: {
      verdicts: comprehension
        ? [...investVerdicts, comprehension]
        : investVerdicts,
      score,
    },
    ...(comprehension ? { comprehension } : {}),
  };
};
